/**
 * Overview tab: the manager's planning-and-oversight view of a project.
 * Fetches its own data; ignores props.tasks.
 */
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { differenceInCalendarDays, format } from 'date-fns';
import { ArrowLeft, Diamond, User } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import SearchableSelect from '@/client/components/admin/SearchableSelect';
import { useTaskOverviewQuery } from '@/client/api/tasks/tasks';
import type { OwnerLoad, TaskPerformance, TaskPriority, TaskStatus } from '@/client/api/tasks/tasks';
import { Avatar, PRIORITY, PRIORITY_ORDER, PriorityPill, STATUS, STATUS_ORDER, fmtDay, personName, personOptions } from './taskUi';
import type { TaskViewProps } from './taskUi';

const Card: React.FC<{ title: string; className?: string; children: React.ReactNode }> = ({ title, className = '', children }) => (
  <section className={`rounded-xl border border-gray-200 bg-white p-4 ${className}`}>
    <h3 className="mb-3 text-sm font-semibold text-gray-900">{title}</h3>
    {children}
  </section>
);

const Kpi: React.FC<{ label: string; value: string | number; tone?: string }> = ({ label, value, tone = 'text-gray-900' }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4">
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <p className={`mt-1 text-2xl font-semibold tabular-nums ${tone}`}>{value}</p>
  </div>
);

/** Per-owner stacked bar: in progress / blocked / other open. */
const LoadBar: React.FC<{ o: OwnerLoad; max: number }> = ({ o, max }) => {
  const other = Math.max(0, o.open - o.inProgress - o.blocked);
  const seg = (n: number, cls: string, label: string) =>
    n > 0 ? <div className={cls} style={{ width: `${(n / max) * 100}%` }} title={`${label}: ${n}`} /> : null;
  return (
    <div className="flex h-2 w-full gap-0.5 overflow-hidden rounded-full bg-gray-50">
      {seg(o.inProgress, 'bg-emerald-500', 'In progress')}
      {seg(o.blocked, 'bg-red-500', 'Blocked')}
      {seg(other, 'bg-gray-300', 'Other open')}
    </div>
  );
};

const hours = (n: number) => `${Math.round(n * 10) / 10}h`;
const days = (n: number | null | undefined) => (n == null ? '—' : `${n}d`);
const pct = (n: number | null | undefined) => (n == null ? '—' : `${n}%`);

/** 12 weeks of created vs done, as paired bars. */
const Throughput: React.FC<{ perf: TaskPerformance }> = ({ perf }) => {
  const max = Math.max(1, ...perf.weekly.flatMap((w) => [w.created, w.done]));
  return (
    <div>
      <div className="flex h-32 items-end gap-1.5" role="img" aria-label="Tasks created and done per week, last 12 weeks">
        {perf.weekly.map((w) => (
          <div key={w.weekStart} className="flex flex-1 items-end justify-center gap-0.5" title={`Week of ${w.weekStart}: ${w.created} created, ${w.done} done`}>
            <div className="w-1/2 rounded-t bg-gray-300" style={{ height: `${(w.created / max) * 100}%`, minHeight: w.created ? 2 : 0 }} />
            <div className="w-1/2 rounded-t bg-blue-500" style={{ height: `${(w.done / max) * 100}%`, minHeight: w.done ? 2 : 0 }} />
          </div>
        ))}
      </div>
      <div className="mt-1 flex gap-1.5 text-[10px] text-gray-400">
        {perf.weekly.map((w, i) => (
          <span key={w.weekStart} className="flex-1 text-center">{i % 2 === 0 ? format(new Date(`${w.weekStart}T00:00:00`), 'MMM d') : ''}</span>
        ))}
      </div>
      <div className="mt-2 flex gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-sm bg-gray-300" />Created</span>
        <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-sm bg-blue-500" />Done</span>
      </div>
    </div>
  );
};

const OverviewView: React.FC<TaskViewProps> = (props) => {
  const { project, meta, onOpen } = props;
  const [params, setParams] = useSearchParams();
  const person = params.get('person') ?? '';
  const setPerson = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set('person', value);
    else next.delete('person');
    setParams(next, { replace: true });
  };
  const { data, isLoading, isError } = useTaskOverviewQuery(project?.id, person || undefined);
  const people = meta?.people;
  const personUid = person === 'none' ? null : person ? Number(person) : undefined;
  const header = (
    <div className="flex flex-wrap items-center gap-3">
      {person && (
        <button type="button" onClick={() => setPerson('')} className="flex items-center gap-1 text-sm text-blue-600 hover:underline">
          <ArrowLeft className="h-3.5 w-3.5" /> Team
        </button>
      )}
      <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        {personUid !== undefined && <Avatar people={people} uid={personUid} size={24} />}
        {person ? `${personName(people, personUid ?? null)}'s tasks` : project ? `${project.name}: team` : 'All projects: team'}
      </h2>
      <SearchableSelect
        className="ml-auto w-64"
        icon={User}
        placeholder="Look at one person…"
        value={person}
        onChange={setPerson}
        options={personOptions(people, [{ value: 'none', label: 'Unowned tasks' }])}
      />
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        {header}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-xl" />
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
      </div>
    );
  }
  if (isError || !data) {
    return <div className="space-y-4 p-4">{header}<p className="p-8 text-center text-sm text-red-600">Could not load the overview.</p></div>;
  }
  if (data.total === 0) {
    return (
      <div className="space-y-4 p-4">
        {header}
        <p className="p-8 text-center text-sm text-gray-500">{person ? 'No tasks for this person here yet.' : 'No tasks in this project yet.'}</p>
      </div>
    );
  }
  const perf = data.performance;

  const done7 = data.byOwner.reduce((s, o) => s + o.doneLast7d, 0);
  // Unowned row last, otherwise API order.
  const owners = [...data.byOwner.filter((o) => o.uid != null), ...data.byOwner.filter((o) => o.uid == null)];
  const maxOpen = Math.max(1, ...owners.map((o) => o.open));
  const statusTotal = STATUS_ORDER.reduce((s, k) => s + (data.byStatus[k] ?? 0), 0) || 1;
  const prioMax = Math.max(1, ...PRIORITY_ORDER.map((k) => data.byPriority[k] ?? 0));
  const sectionName = (id: string | null) => (id ? project?.sections?.find((s) => s.id === id)?.name ?? 'Unknown section' : 'No section');
  const today = new Date();
  const daysTo = (day: string) => differenceInCalendarDays(new Date(`${day}T00:00:00`), today);

  return (
    <div className="space-y-4 p-4">
      {header}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Kpi label="Open" value={data.open} />
        <Kpi label="Overdue" value={data.overdue} tone={data.overdue > 0 ? 'text-red-600' : undefined} />
        <Kpi label="Unowned" value={data.unowned} tone={data.unowned > 0 ? 'text-amber-600' : undefined} />
        <Kpi label="Done this week" value={done7} />
        <Kpi label="Open hours" value={hours(data.openHours)} />
        <Kpi label="Avg progress" value={data.avgProgress == null ? '—' : `${Math.round(data.avgProgress)}%`} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Delivery, last 12 weeks" className="lg:col-span-2">
          <Throughput perf={perf} />
        </Card>
        <Card title="How work gets done">
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs text-gray-500">Done in 30 days</dt>
              <dd className="text-xl font-semibold tabular-nums">{perf.doneLast30d}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Days to close (median)</dt>
              <dd className="text-xl font-semibold tabular-nums">{days(perf.cycleDays.median)}</dd>
              <dd className="text-xs text-gray-400">avg {days(perf.cycleDays.average)} · {perf.cycleDays.count} tasks</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">On time</dt>
              <dd className={`text-xl font-semibold tabular-nums ${perf.onTime.rate != null && perf.onTime.rate < 70 ? 'text-amber-600' : ''}`}>{pct(perf.onTime.rate)}</dd>
              <dd className="text-xs text-gray-400">{perf.onTime.onTime} of {perf.onTime.withTarget} with a target date</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Oldest open task</dt>
              <dd className="text-xl font-semibold tabular-nums">{days(perf.oldestOpenDays)}</dd>
            </div>
          </dl>
        </Card>
      </div>

      {!person && <Card title="Team load">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs font-medium text-gray-500">
                <th className="py-2 pr-3">Owner</th>
                <th className="px-2 text-right">Open</th>
                <th className="px-2 text-right">In progress</th>
                <th className="px-2 text-right">Blocked</th>
                <th className="px-2 text-right">Overdue</th>
                <th className="px-2 text-right">Done 7d</th>
                <th className="px-2 text-right">Done 30d</th>
                <th className="px-2 text-right" title="Median days from filed to done">Days to close</th>
                <th className="px-2 text-right" title="Done by the target date">On time</th>
                <th className="px-2 text-right">Open hours</th>
                <th className="w-40 pl-3">Mix</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {owners.map((o) => (
                <tr
                  key={o.uid ?? 'none'}
                  tabIndex={0}
                  title="See this person's tasks"
                  onClick={() => setPerson(o.uid == null ? 'none' : String(o.uid))}
                  onKeyDown={(e) => e.key === 'Enter' && setPerson(o.uid == null ? 'none' : String(o.uid))}
                  className="cursor-pointer border-b border-gray-50 last:border-0 hover:bg-gray-50"
                >
                  <td className="py-2 pr-3">
                    <span className="flex items-center gap-2">
                      <Avatar people={people} uid={o.uid} size={22} />
                      <span className={o.uid == null ? 'text-gray-500' : 'text-gray-900'}>{personName(people, o.uid)}</span>
                    </span>
                  </td>
                  <td className="px-2 text-right">{o.open}</td>
                  <td className="px-2 text-right">{o.inProgress}</td>
                  <td className="px-2 text-right">{o.blocked}</td>
                  <td className={`px-2 text-right ${o.overdue > 0 ? 'font-medium text-red-600' : ''}`}>{o.overdue}</td>
                  <td className="px-2 text-right">{o.doneLast7d}</td>
                  <td className="px-2 text-right">{o.doneLast30d}</td>
                  <td className="px-2 text-right">{days(o.medianCycleDays)}</td>
                  <td className={`px-2 text-right ${o.onTimeRate != null && o.onTimeRate < 70 ? 'text-amber-600' : ''}`}>{pct(o.onTimeRate)}</td>
                  <td className="px-2 text-right">{hours(o.openHours)}</td>
                  <td className="pl-3">
                    <LoadBar o={o} max={maxOpen} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-emerald-500" />In progress</span>
          <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-red-500" />Blocked</span>
          <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-gray-300" />Other open</span>
          <span className="ml-auto">Click a person to see their tasks.</span>
        </div>
      </Card>}

      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
        <Card title="Status mix">
          <div className="flex h-3 w-full gap-0.5 overflow-hidden rounded-full bg-gray-50">
            {STATUS_ORDER.map((k: TaskStatus) =>
              (data.byStatus[k] ?? 0) > 0 ? (
                <div key={k} className={STATUS[k].bar} style={{ width: `${((data.byStatus[k] ?? 0) / statusTotal) * 100}%` }} title={`${STATUS[k].label}: ${data.byStatus[k]}`} />
              ) : null,
            )}
          </div>
          <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
            {STATUS_ORDER.map((k) => (
              <li key={k} className="flex items-center gap-2">
                <i className={`h-2 w-2 shrink-0 rounded-full ${STATUS[k].bar}`} />
                <span className="flex-1">{STATUS[k].label}</span>
                <span className="tabular-nums text-gray-900">{data.byStatus[k] ?? 0}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Open by priority">
          <ul className="space-y-1.5">
            {PRIORITY_ORDER.map((k: TaskPriority) => {
              const n = data.byPriority[k] ?? 0;
              return (
                <li key={k} className="flex items-center gap-2 text-xs">
                  <span className="w-20 shrink-0 text-gray-600">{PRIORITY[k].label}</span>
                  <div className="h-2 flex-1 rounded-full bg-gray-50">
                    <div className="h-2 rounded-full bg-blue-400" style={{ width: `${(n / prioMax) * 100}%` }} />
                  </div>
                  <span className="w-8 text-right tabular-nums text-gray-900">{n}</span>
                </li>
              );
            })}
          </ul>
        </Card>

        {project && (
          <Card title="Sections" className="lg:col-span-2 xl:col-span-1">
            <ul className="space-y-3">
              {data.bySection.map((s) => {
                const tot = s.open + s.closed;
                return (
                  <li key={s.sectionId ?? 'none'}>
                    <div className="mb-1 flex justify-between text-xs">
                      <span className="truncate text-gray-700">{sectionName(s.sectionId)}</span>
                      <span className="shrink-0 tabular-nums text-gray-500">{s.closed} of {tot} done</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-gray-100">
                      <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${tot ? (s.closed / tot) * 100 : 0}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title={`Overdue (${data.overdue})`} className="lg:col-span-2">
          {data.overdueTasks.length === 0 ? (
            <p className="text-sm text-gray-400">Nothing overdue.</p>
          ) : (
            <ul className="divide-y divide-gray-50">
              {data.overdueTasks.slice(0, 20).map((t) => (
                <li key={t.id}>
                  <button type="button" onClick={() => onOpen(t)} className="flex w-full items-center gap-3 py-2 text-left hover:bg-gray-50">
                    <span className="w-14 shrink-0 text-xs text-gray-400">T{t.number}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{t.title}</span>
                    <Avatar people={people} uid={t.ownerUid} size={20} />
                    <span className="w-16 shrink-0 text-right text-xs text-red-600">{fmtDay(t.dueDate)}</span>
                    <PriorityPill priority={t.priority} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {person && (
          <Card title="Recently done" className="lg:col-span-2">
            {data.recentDone.length === 0 ? (
              <p className="text-sm text-gray-400">Nothing finished yet.</p>
            ) : (
              <ul className="divide-y divide-gray-50">
                {data.recentDone.map((t) => (
                  <li key={t.id}>
                    <button type="button" onClick={() => onOpen(t)} className="flex w-full items-center gap-3 py-2 text-left hover:bg-gray-50">
                      <span className="w-14 shrink-0 text-xs text-gray-400">T{t.number}</span>
                      <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{t.title}</span>
                      <span className="shrink-0 text-xs text-gray-500">{t.closedAt ? fmtDay(t.closedAt.slice(0, 10)) : ''}</span>
                      {t.dueDate && t.closedAt && (
                        <span className={`w-16 shrink-0 text-right text-xs ${t.closedAt.slice(0, 10) <= t.dueDate ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {t.closedAt.slice(0, 10) <= t.dueDate ? 'on time' : 'late'}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        <Card title="Upcoming milestones">
          {data.milestones.length === 0 ? (
            <p className="text-sm text-gray-400">No milestones.</p>
          ) : (
            <ul className="space-y-2">
              {data.milestones.map((m) => {
                const d = m.dueDate ? daysTo(m.dueDate) : null;
                return (
                  <li key={m.id}>
                    <button type="button" onClick={() => onOpen(m)} className="flex w-full items-center gap-2 text-left hover:bg-gray-50">
                      <Diamond className="h-3.5 w-3.5 shrink-0 text-violet-500" />
                      <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{m.title}</span>
                      <span className="shrink-0 text-xs text-gray-500">{fmtDay(m.dueDate)}</span>
                      {d != null && (
                        <span className={`w-14 shrink-0 text-right text-xs ${d < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {d < 0 ? `${-d}d late` : d === 0 ? 'today' : `${d}d to go`}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};

export default OverviewView;
